<template>
  <div class="schedule-wrapper" id="lich-hoat-dong">
    <div class="schedule-container">

      <!-- Header -->
      <div class="schedule-header">
        <div class="header-text-area">
          <h2 class="schedule-title">{{ $t('home.schedule.title') || 'Lịch Sự Kiện Nổi Bật' }}</h2>
          <p class="schedule-desc">{{ $t('home.schedule.desc') }}</p>
        </div>
        <button @click="goToEventPage" class="btn-view-all">
          {{ $t('home.schedule.all_events') || 'Tất cả sự kiện' }}
          <span class="material-symbols-outlined">arrow_forward</span>
        </button>
      </div>

      <!-- Tabs -->
      <div class="schedule-tabs-container">
        <button v-for="tab in displayTabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['schedule-tab-btn', { active: activeTab === tab.id }]">
          {{ tab.name }}
        </button>
      </div>

      <div class="schedule-content">
        <!-- 1. Xuyên suốt -->
        <div v-if="activeTab === 'xuyenSuot'" class="schedule-grid-2">
          <div v-for="(item, index) in displayOngoing" :key="item.id || index" class="xs-card">
            <div class="xs-badge">
              {{ formatSTT(item.stt) }} </div>
            <div class="xs-info text-left">
              <h4 class="xs-title">{{ item.tenHoatDong }}</h4>
              <p class="xs-location" v-if="item.diaDiem">
                <span class="highlight-location">{{ $t('home.schedule.location') || 'Địa điểm' }}:</span> {{
                  item.diaDiem }}
              </p>
              <ul class="xs-list" v-if="item.hoatDongCon && item.hoatDongCon.length">
                <li v-for="(sub, idx) in item.hoatDongCon" :key="idx">
                  <span class="bullet-point">•</span> {{ sub }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- 2. Hoạt động chính -->
        <div v-if="activeTab === 'chinh'" class="schedule-list">
          <div v-for="(item, index) in displayMain" :key="item.id || index" class="main-card">
            <div class="main-card-header">
              <span class="main-card-title">{{ $t('home.schedule.main_activity') || 'Hoạt động chính' }} {{ item.stt
                }}</span>
              <span class="main-card-badge">{{ $t('home.schedule.focus') || 'Trọng tâm' }}</span>
            </div>
            <div class="main-card-body">
              <div class="main-card-left text-left">
                <h3 class="main-event-title">{{ item.tenHoatDong }}</h3>
                <p class="main-event-row" v-if="item.diaDiem"><strong>{{ $t('home.schedule.location') || 'Địa điểm'
                    }}:</strong> {{ item.diaDiem }}</p>
                <p class="main-event-row" v-if="item.chuTri"><strong>{{ $t('home.schedule.host') || 'Chủ trì'
                    }}:</strong> {{ formatPhoiHop(item.chuTri)
                    }}</p>
                <p class="main-event-row" v-if="item.daiBieu"><strong>{{ $t('home.schedule.delegates') || 'Đại biểu'
                    }}:</strong> {{ item.daiBieu }}</p>
                <div class="main-event-tags">
                  <span class="tag-gray" v-if="item.thamMuu"><strong>{{ $t('home.schedule.advise') || 'Tham mưu'
                      }}:</strong> {{ item.thamMuu }}</span>
                  <span class="tag-gray" v-if="item.phoiHop">
                    <strong>{{ $t('home.schedule.cooperate') || 'Phối hợp' }}:</strong> {{ formatPhoiHop(item.phoiHop)
                    }}
                  </span>
                </div>
              </div>
              <div class="main-card-right">
                <h4 class="detail-label text-left">{{ $t('home.schedule.detail_content') || 'Chi tiết nội dung' }}</h4>
                <div v-if="item.noiDungs && item.noiDungs.length">
                  <ul class="list-unstyled ps-0 mb-0 text-left">
                    <li v-for="(nd, ndIdx) in item.noiDungs" :key="ndIdx"
                      class="d-flex align-items-start gap-2 mb-1 small text-dark">
                      <span class="bullet-point">•</span>
                      <span>{{ nd.text }}</span>
                    </li>
                  </ul>
                </div>
                <div v-else class="ck-content text-left" v-html="item.noiDung"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 3. Khung giờ -->
        <div v-if="activeTab === 'khungGio'" class="schedule-list">
          <div v-for="(dayGroup, groupIdx) in displayDaily" :key="groupIdx" class="day-group mb-4">
            <h4 class="day-group-title text-success font-heading fw-bold mb-3 mt-2 text-left"
              style="font-size: 1.15rem;">
              {{ $t('home.schedule.day') || 'Ngày' }}
              {{ dayGroup.date }} ({{ dayGroup.dayName }})
            </h4>
            <div class="schedule-list-inner">
              <div v-for="(item, idx) in dayGroup.events" :key="item.id || idx" class="kg-card mb-3">
                <div class="kg-left">
                  <div class="kg-date-badge" :class="'badge-' + ((groupIdx + idx) % 3)">
                    <span class="kg-day">{{ dayGroup.date.split('/')[0] }}</span>
                    <span class="kg-month">{{ $t('home.schedule.month') || 'Tháng' }} {{
                      parseInt(dayGroup.date.split('/')[1]) }}</span>
                  </div>
                  <div class="kg-info text-left">
                    <span class="kg-tag" :class="'tag-' + (idx % 3)" v-if="item.chuTri">
                      {{ formatPhoiHop(item.chuTri) }}
                    </span>
                    <h4 class="kg-title">{{ item.name }}</h4>
                    <p class="kg-location" v-if="item.diaChi">
                      <span class="material-symbols-outlined icon">location_on</span>
                      {{ item.diaChi }}
                    </p>
                  </div>
                </div>
                <div class="kg-right">
                  <div class="kg-time">
                    <span class="btn-detail">
                      <span class="material-symbols-outlined icon me-1">schedule</span>
                      {{ item.gioBatDau }}{{ item.gioKetThuc ? ' - ' + item.gioKetThuc : '' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. Hưởng ứng -->
        <div v-if="activeTab === 'huongUng'" class="schedule-list">
          <div v-for="(item, idx) in displaySupport" :key="idx" class="hu-card">
            <div class="hu-header">
              <h3 class="hu-title text-left">{{ item.tieuDeHoatDong }}</h3>
              <span class="hu-time">⏱ {{ item.thoiGian }}</span>
            </div>
            <div class="hu-body">
              <div class="hu-left text-left">
                <p v-if="item.chuTri"><strong> {{ $t('home.schedule.host_unit') || 'Đơn vị chủ trì' }}:</strong> {{
                  formatPhoiHop(item.chuTri) }}</p>
                <p v-if="item.phoiHop"><strong>{{ $t('home.schedule.cooperating_unit') || 'Đơn vị phối hợp'
                    }}:</strong> {{ formatPhoiHop(item.phoiHop) }}</p>
                <p v-if="item.diaDiem"><strong> {{ $t('home.schedule.location') || 'Địa điểm' }}:</strong> {{
                  item.diaDiem }}</p>
              </div>
              <div class="hu-right">
                <h4 class="detail-label text-left">{{ $t('home.schedule.support_detail') }}</h4>
                <div v-if="item.noiDungs && item.noiDungs.length">
                  <ul class="list-unstyled ps-0 mb-0 text-left">
                    <li v-for="(nd, ndIdx) in item.noiDungs" :key="ndIdx"
                      class="d-flex align-items-start gap-2 mb-1  text-dark">
                      <span class="bullet-point">•</span>
                      <span>{{ nd.text }}</span>
                    </li>
                  </ul>
                </div>
                <div v-else class="ck-content text-left" v-html="item.noiDungHoatDong"></div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "LichHoatDong",
  props: {
    ongoingActivities: {
      type: Array,
      default: () => [],
    },
    dailyPrograms: {
      type: Array,
      default: () => [],
    },
    mainActivities: {
      type: Array,
      default: () => [],
    },
    supportActivities: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeTab: "khungGio",
      tabs: [
        { id: 'khungGio', name: 'Lịch Theo Khung Giờ' },
        { id: 'xuyenSuot', name: 'Hoạt Động Xuyên Suốt' },
        { id: 'chinh', name: 'Hoạt Động Chính' },
        { id: 'huongUng', name: 'Hoạt Động Hưởng Ứng' }
      ],
      staticData: {
        hoatDongXuyenSuot: [
          {
            stt: 1,
            tenHoatDong: "Không gian trưng bày và giới thiệu Trái cây đặc sản Đồng Tháp",
            diaDiem: "Khuôn viên Công viên Văn Miếu, TP. Cao Lãnh",
            hoatDongCon: [
              "Trưng bày 100 gian hàng trái cây VietGAP/GlobalGAP",
              "Khu vực check-in Đại cảnh Bản đồ Trái cây vùng Đất Sen Hồng",
              "Hoạt động nếm thử trái cây miễn phí vào khung giờ vàng",
            ],
          },
          {
            stt: 2,
            tenHoatDong: "Hội chợ Triển lãm Nông nghiệp Công nghệ cao",
            diaDiem: "Trung tâm Huấn luyện và Thi đấu Thể thao Tỉnh",
            hoatDongCon: [
              "Giới thiệu drone sạ phân, phun thuốc thế hệ mới",
              "Hệ thống tưới tự động thông minh AI",
            ],
          },
        ],
        hoatDongChinh: [
          {
            stt: 1,
            tenHoatDong: "LỄ KHAI MẠC & DIỄN HÀNH TRÁI CÂY",
            diaDiem: "Quảng trường Hùng Vương, Phường Đạo Thạnh",
            chuTri: "UBND Tỉnh Đồng Tháp",
            daiBieu: "Lãnh đạo Bộ NN&PTNT, Lãnh đạo Tỉnh ủy, các doanh nghiệp lữ hành và xuất nhập khẩu",
            thamMuu: "Sở Nông nghiệp & PTNT",
            phoiHop: [
              { id: 2, name: "UBND TP. Cao Lãnh" },
              { id: 3, name: "Sở Văn hóa, Thể thao & Du lịch" },
            ],
            noiDung: `<p>Lễ khai mạc chính thức mở màn cho chuỗi sự kiện <strong>Lễ hội Trái cây 2026</strong>. Sự kiện bao gồm:</p>
                      <ul>
                        <li>Chương trình nghệ thuật tổng hợp "Mùa vàng Đất Sen Hồng".</li>
                        <li>Diễn hành 20 xe hoa trang trí bằng trái cây tươi đặc sản độc đáo.</li>
                      </ul>`,
          },
        ],
        hoatDongKhungGio: [
          {
            thoiGian: "08:00 - 10:30",
            tieuDeHoatDong: "Lễ Khai Mạc & Diễn Hành Trái Cây",
            diaDiem: "Quảng trường Hùng Vương, Phường Đạo Thạnh",
          },
          {
            thoiGian: "14:00 - 17:00",
            tieuDeHoatDong: "Hội Thi Trình Diễn Nghệ Thuật Tạo Hình Trái Cây",
            diaDiem: "Công viên Hai Bà Trưng, TP. Cao Lãnh",
          },
          {
            thoiGian: "Cả ngày",
            tieuDeHoatDong: "Chợ Phiên Nông Sản Của Hợp Tác Xã",
            diaDiem: "Bờ sông Tiền, TP. Cao Lãnh",
          },
        ],
        hoatDongHuongUng: [
          {
            tieuDeHoatDong: "Tuần lễ ẩm thực các món ngon từ Trái Cây kết hợp Sen Đồng Tháp",
            thoiGian: "Từ ngày 05/06 đến ngày 10/06/2026",
            chuTri: [{ id: 3, name: "Sở Văn hóa, Thể thao & Du lịch" }],
            phoiHop: [{ id: 2, name: "UBND TP. Cao Lãnh" }],
            noiDungHoatDong: `<p>Chuỗi nhà hàng, khách sạn trên địa bàn đồng loạt giảm giá 10% và ra mắt menu đặc sản ứng dụng <em>Xoài Cao Lãnh, Quýt hồng Lai Vung</em> vào các món ăn mặn và tráng miệng.</p>`,
          },
        ],
      }
    };
  },
  computed: {
    displayTabs() {
      return [
        { id: 'khungGio', name: this.$t('home.tabs.khungGio') || 'Lịch Theo Khung Giờ' },
        { id: 'xuyenSuot', name: this.$t('home.tabs.xuyenSuot') || 'Hoạt Động Xuyên Suốt' },
        { id: 'chinh', name: this.$t('home.tabs.chinh') || 'Hoạt Động Chính' },
        { id: 'huongUng', name: this.$t('home.tabs.huongUng') || 'Hoạt Động Hưởng Ứng' }
      ];
    },
    localizedStaticData() {
      const currentLocale = this.$i18n ? this.$i18n.locale : "vi";
      if (currentLocale === "en") {
        return {
          hoatDongXuyenSuot: [
            {
              stt: 1,
              tenHoatDong: "Dong Thap Specialty Fruit Exhibition Space",
              diaDiem: "Van Mieu Park Grounds, Cao Lanh City",
              hoatDongCon: [
                "Display 100 stalls of VietGAP/GlobalGAP fruits",
                "Check-in area at the Fruit Map of Lotus Land",
                "Free fruit tasting during golden hours",
              ],
            },
            {
              stt: 2,
              tenHoatDong: "High-Tech Agricultural Exhibition Fair",
              diaDiem: "Provincial Sports Training and Competition Center",
              hoatDongCon: [
                "Introducing new generation fertilizer and pesticide drones",
                "Smart AI automatic irrigation system",
              ],
            },
          ],
          hoatDongChinh: [
            {
              stt: 1,
              tenHoatDong: "OPENING CEREMONY & FRUIT PARADE",
              diaDiem: "Hung Vuong Square, Dao Thanh Ward",
              chuTri: "People's Committee of Dong Thap Province",
              daiBieu: "Leaders of MARD, Provincial Party Committee, travel and import-export enterprises",
              thamMuu: "Department of Agriculture & Rural Development",
              phoiHop: [
                { id: 2, name: "People's Committee of Cao Lanh City" },
                { id: 3, name: "Department of Culture, Sports & Tourism" },
              ],
              noiDung: `<p>Official opening ceremony kicks off the series of events for the <strong>2026 Fruit Festival</strong>. The event includes:</p>
                        <ul>
                          <li>Art program "Lotus Land Golden Harvest".</li>
                          <li>Parade of 20 floats decorated with fresh specialty fruits.</li>
                        </ul>`,
            },
          ],
          hoatDongKhungGio: [
            {
              thoiGian: "08:00 - 10:30",
              tieuDeHoatDong: "Opening Ceremony & Fruit Parade",
              diaDiem: "Hung Vuong Square, Dao Thanh Ward",
            },
            {
              thoiGian: "14:00 - 17:00",
              tieuDeHoatDong: "Fruit Carving Art Performance Competition",
              diaDiem: "Hai Ba Trung Park, Cao Lanh City",
            },
            {
              thoiGian: "All day",
              tieuDeHoatDong: "Farmers' Cooperative Agricultural Fair",
              diaDiem: "Tien Riverbank, Cao Lanh City",
            },
          ],
          hoatDongHuongUng: [
            {
              tieuDeHoatDong: "Food Week featuring Specialty Fruits & Lotus of Dong Thap",
              thoiGian: "From June 5th to June 10th, 2026",
              chuTri: [{ id: 3, name: "Department of Culture, Sports & Tourism" }],
              phoiHop: [{ id: 2, name: "People's Committee of Cao Lanh City" }],
              noiDungHoatDong: `<p>Restaurants and hotels in the area offer a 10% discount and launch a specialty menu using <em>Cao Lanh Mango, Lai Vung Pink Mandarin</em> in main courses and desserts.</p>`,
            },
          ],
        };
      }
      return this.staticData;
    },
    displayOngoing() {
      if (this.ongoingActivities && this.ongoingActivities.length > 0) {
        return this.ongoingActivities.map((e, index) => ({
          id: e.id || index,
          stt: e.stt || (index + 1),
          tenHoatDong: this.getLangValue(e, 'name') || this.getLangValue(e, 'tenHoatDong'),
          diaDiem: this.getLangValue(e, 'diaChi') || this.getLangValue(e, 'diaDiem'),
          hoatDongCon: e.noiDungs ? e.noiDungs.map(nd => this.getLangValue(nd, 'text')) : (e.hoatDongCon || [])
        }));
      }
      return this.localizedStaticData.hoatDongXuyenSuot;
    },
    displayDaily() {
      if (this.dailyPrograms && this.dailyPrograms.length > 0) {
        return this.dailyPrograms.map(group => ({
          ...group,
          dayName: this.getDayName(group.dayName),
          events: group.events.map(e => ({
            ...e,
            name: this.getLangValue(e, 'name') || this.getLangValue(e, 'tieuDeHoatDong'),
            diaChi: this.getLangValue(e, 'diaChi') || this.getLangValue(e, 'diaDiem'),
            chuTri: this.getLangValue(e, 'chuTri'),
            phoiHop: this.getLangValue(e, 'phoiHop')
          }))
        }));
      }
      return [
        {
          date: "05/06",
          dayName: this.getDayName("Friday"),
          events: this.localizedStaticData.hoatDongKhungGio.map((e, idx) => ({
            id: idx,
            name: e.tieuDeHoatDong,
            diaChi: e.diaDiem,
            gioBatDau: e.thoiGian.split(' - ')[0] || e.thoiGian,
            gioKetThuc: e.thoiGian.split(' - ')[1] || '',
            chuTri: idx === 0 ? 'Lễ khai mạc' : idx === 1 ? 'Nghệ thuật' : 'Giao thương'
          }))
        }
      ];
    },
    displayMain() {
      if (this.mainActivities && this.mainActivities.length > 0) {
        return this.mainActivities.map((e, index) => ({
          id: e.id || index,
          stt: e.stt || (index + 1),
          tenHoatDong: this.getLangValue(e, 'name') || this.getLangValue(e, 'tenHoatDong'),
          diaDiem: this.getLangValue(e, 'diaChi') || this.getLangValue(e, 'diaDiem'),
          chuTri: this.getLangValue(e, 'chuTri'),
          daiBieu: this.getLangValue(e, 'daiBieu'),
          thamMuu: this.getLangValue(e, 'thamMuu'),
          phoiHop: this.getLangValue(e, 'phoiHop'),
          noiDungs: e.noiDungs ? e.noiDungs.map(nd => ({ ...nd, text: this.getLangValue(nd, 'text') })) : [],
          noiDung: this.getLangValue(e, 'noiDung')
        }));
      }
      return this.localizedStaticData.hoatDongChinh;
    },
    displaySupport() {
      if (this.supportActivities && this.supportActivities.length > 0) {
        return this.supportActivities.map((e, index) => ({
          id: e.id || index,
          tieuDeHoatDong: this.getLangValue(e, 'tieuDeHoatDong') || this.getLangValue(e, 'name'),
          thoiGian: this.getLangValue(e, 'thoiGian'),
          diaDiem: this.getLangValue(e, 'diaChi') || this.getLangValue(e, 'diaDiem'),
          chuTri: this.getLangValue(e, 'chuTri'),
          phoiHop: this.getLangValue(e, 'phoiHop'),
          noiDungs: e.noiDungs ? e.noiDungs.map(nd => ({ ...nd, text: this.getLangValue(nd, 'text') })) : [],
          noiDungHoatDong: this.getLangValue(e, 'noiDungHoatDong') || this.getLangValue(e, 'noiDung')
        }));
      }
      return this.localizedStaticData.hoatDongHuongUng;
    }
  },
  methods: {
    getLangValue(item, fieldName) {
      if (!item) return "";
      const currentLocale = this.$i18n ? this.$i18n.locale : "vi";
      if (currentLocale === "en") {
        const enField = `${fieldName}En`;
        return item[enField] || item[fieldName] || "";
      }
      return item[fieldName] || "";
    },
    getDayName(dayName) {
      if (!dayName) return "";
      const currentLocale = this.$i18n ? this.$i18n.locale : "vi";
      if (currentLocale === "vi") return dayName;
      const viToEn = {
        "Thứ Hai": "Monday",
        "Thứ Ba": "Tuesday",
        "Thứ Tư": "Wednesday",
        "Thứ Năm": "Thursday",
        "Thứ Sáu": "Friday",
        "Thứ Bảy": "Saturday",
        "Chủ Nhật": "Sunday",
        "Monday": "Monday",
        "Tuesday": "Tuesday",
        "Wednesday": "Wednesday",
        "Thursday": "Thursday",
        "Friday": "Friday",
        "Saturday": "Saturday",
        "Sunday": "Sunday"
      };
      return viToEn[dayName] || dayName;
    },
    async getHoatDong() {
      try {
        await this.$store?.dispatch('loadingStore/show');

        const result = await this.chuongTrinhModel.getAll();
        if (result?.status == 200) {
          this.activities = result.data.map((item) =>
            this.chuongTrinhModel.fromJson(item)
          );
          this.totalCount = result.count;
        } else {
          this.$store?.dispatch("snackBarStore/addNotify", {
            message: result?.message || "Lỗi hệ thống",
            code: result?.status || 500
          });
        }

        await this.$store?.dispatch('loadingStore/hide');
      } catch (error) {
        console.error('Error fetching activities:', error);
        await this.$store?.dispatch('loadingStore/hide');
      }
    },

    goToEventPage() {
      this.$router.push("/lich-hoat-dong");
    },
    formatSTT(stt) {
      if (!stt) return '';
      return stt < 10 ? `0${stt}` : stt;
    },
    formatPhoiHop(val) {
      if (!val) return "";
      if (Array.isArray(val)) {
        return val.map((d) => this.getLangValue(d, 'name') || d).join(", ");
      }
      return val;
    }
  }
};
</script>

<style scoped>
/* GENERAL WRAPPER */
.schedule-wrapper {
  background-color: var(--color-public-bg, #f9fbf9);
  padding: 80px 0;
  font-family: var(--font-body, "Inter");
}

.schedule-container {
  max-width: var(--container-max, 1200px);
  margin: 0 auto;
  padding: 0 24px;
}

/* HEADER */
.schedule-header {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-left: 4px solid var(--color-public-primary, #0d631b);
  padding-left: 16px;
  margin-bottom: 40px;
  gap: 16px;
}

@media (min-width: 768px) {
  .schedule-header {
    flex-direction: row;
    align-items: flex-end;
  }
}

.schedule-title {
  font-family: var(--font-heading, "Montserrat");
  font-weight: 800;
  font-size: var(--text-headline-md-size, 2.29rem);
  color: var(--color-public-text-head, #1f2937);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.schedule-desc {
  font-size: var(--text-body-sm-size, 1rem);
  color: var(--color-public-text-body, #6b7280);
  margin: 4px 0 0 0;
}

.btn-view-all {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: var(--color-public-primary, #0d631b);
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  font-size: 1rem;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s;
}

.btn-view-all:hover {
  opacity: 0.8;
}

/* TABS */
.schedule-tabs-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 32px;
  border-bottom: 1px solid var(--surface-dim, #e5e7eb);
  padding-bottom: 16px;
}

.schedule-tab-btn {
  padding: 10px 20px;
  border-radius: 9999px;
  font-family: var(--font-heading, "Montserrat");
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s ease;
  background-color: #ffffff;
  color: var(--color-public-text-body, #4b5563);
  border: 1px solid var(--surface-dim, #e5e7eb);
  cursor: pointer;
}

.schedule-tab-btn:hover {
  background-color: #f3f4f6;
}

.schedule-tab-btn.active {
  background-color: var(--color-public-primary, #0d631b);
  color: #ffffff;
  border-color: var(--color-public-primary, #0d631b);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* GRID LAYOUT */
.schedule-grid-2 {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 768px) {
  .schedule-grid-2 {
    grid-template-columns: repeat(2, 1fr);
  }
}

.schedule-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* CARDS COMMON */
.xs-card,
.main-card,
.kg-card,
.hu-card {
  background-color: #ffffff;
  border: 1px solid var(--surface-dim, #f3f4f6);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border-radius: 16px;
  transition: box-shadow 0.2s ease;
}

.xs-card:hover,
.main-card:hover,
.kg-card:hover,
.hu-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

/* Xuyên Suốt */
.xs-card {
  display: flex;
  gap: 16px;
  padding: 24px;
}

.xs-badge {
  width: 48px;
  height: 48px;
  background-color: rgba(13, 99, 27, 0.1);
  color: var(--color-public-primary, #0d631b);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  font-size: 1.29rem;
  flex-shrink: 0;
}

.xs-info {
  display: flex;
  flex-direction: column;
}

.xs-title {
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  font-size: 1.29rem;
  color: var(--color-public-text-head, #1f2937);
  margin: 0 0 4px 0;
}

.xs-location {
  font-size: 0.95rem;
  color: var(--color-public-text-body, #6b7280);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.highlight-location {
  color: var(--color-public-secondary, #e39434);
  font-weight: 600;
}

.xs-list {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--surface-dim, #e5e7eb);
  list-style: none;
  padding-left: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.xs-list li {
  font-size: 1rem;
  color: var(--color-public-text-body, #4b5563);
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.bullet-point {
  color: var(--color-public-primary, #0d631b);
}

/* Hoạt động chính */
.main-card {
  overflow: hidden;
}

.main-card-header {
  background: linear-gradient(to right, var(--color-public-primary, #0d631b), #047857);
  padding: 16px;
  color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.main-card-title {
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.main-card-badge {
  background-color: var(--color-public-secondary, #e39434);
  color: #ffffff;
  font-size: 0.95rem;
  padding: 4px 12px;
  border-radius: 9999px;
  font-weight: 500;
}

.main-card-body {
  padding: 24px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 992px) {
  .main-card-body {
    grid-template-columns: 2fr 1fr;
  }
}

.main-card-left {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.main-event-title {
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  font-size: 1.43rem;
  color: var(--color-public-text-head, #1f2937);
  margin: 0;
}

.main-event-row {
  font-size: 1rem;
  color: var(--color-public-text-body, #374151);
  margin: 0;
}

.main-event-row strong {
  color: #111827;
}

.main-event-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.tag-gray {
  background-color: #f3f4f6;
  color: #4b5563;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.95rem;
}

.main-card-right {
  background-color: rgba(13, 99, 27, 0.05);
  border: 1px solid rgba(13, 99, 27, 0.1);
  border-radius: 12px;
  padding: 16px;
}

.detail-label {
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--color-public-secondary, #e39434);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 8px 0;
}

/* Khung giờ */
.kg-card {
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
}

@media (min-width: 768px) {
  .kg-card {
    flex-direction: row;
    align-items: flex-start;
  }
}

.kg-left {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.kg-date-badge {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.badge-0 {
  background-color: var(--color-public-text-head, #1f2937);
}

.badge-1 {
  background-color: #ec4899;
}

.badge-2 {
  background-color: var(--color-public-secondary, #e39434);
}

.kg-day {
  font-size: 1.29rem;
  line-height: 1;
}

.kg-month {
  font-size: 0.71rem;
  text-transform: uppercase;
  margin-top: 4px;
  opacity: 0.8;
}

.kg-info {
  display: flex;
  flex-direction: column;
}

.kg-tag {
  font-size: 0.71rem;
  font-family: var(--font-heading, "Montserrat");
  font-weight: 600;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
  align-self: flex-start;
  letter-spacing: 0.05em;
}

.tag-0 {
  background-color: #ecfdf5;
  color: var(--color-public-primary, #0d631b);
}

.tag-1 {
  background-color: #fdf2f8;
  color: #ec4899;
}

.tag-2 {
  background-color: #fff7ed;
  color: var(--color-public-secondary, #e39434);
}

.kg-title {
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  font-size: 1.14rem;
  color: var(--color-public-text-head, #1f2937);
  margin: 4px 0;
}

@media (min-width: 768px) {
  .kg-title {
    font-size: 1.29rem;
  }
}

.kg-location {
  font-size: 0.95rem;
  color: var(--color-public-text-body, #6b7280);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.kg-right {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px dashed var(--surface-dim, #d7dbd2);
  padding-top: 12px;
  gap: 24px;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .kg-right {
    justify-content: flex-end;
    border-top: none;
    padding-top: 0;
  }
}

.kg-time {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--color-public-text-body, #374151);
}

.btn-detail {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  background-color: var(--color-public-text-head, #1f2937);
  color: #ffffff;
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-detail:hover {
  background-color: #374151;
}

.icon-small {
  font-size: 1.14rem;
}

/* Hưởng ứng */
.hu-card {
  padding: 24px;
  border-top: 4px solid #ec4899;
}

.hu-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

@media (min-width: 768px) {
  .hu-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.hu-title {
  font-family: var(--font-heading, "Montserrat");
  font-weight: 700;
  font-size: 1.29rem;
  color: var(--color-public-text-head, #1f2937);
  margin: 0;
}

.hu-time {
  background-color: #fdf2f8;
  color: #ec4899;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 4px 12px;
  border-radius: 9999px;
  align-self: flex-start;
}

.hu-body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 768px) {
  .hu-body {
    grid-template-columns: 1fr 2fr;
  }
}

.hu-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 1rem;
  color: var(--color-public-text-body, #4b5563);
}

@media (min-width: 768px) {
  .hu-left {
    border-right: 1px solid var(--surface-dim, #f3f4f6);
    padding-right: 16px;
  }
}

.hu-left p {
  margin: 0;
}

.hu-right {
  background-color: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid var(--surface-dim, #f3f4f6);
}

/* CKEditor HTML Styles */
:deep(.ck-content) {
  font-size: 1rem;
  color: var(--color-public-text-body, #4b5563);
  line-height: 1.6;
}

:deep(.ck-content) p {
  margin: 0 0 8px 0;
}

:deep(.ck-content) ul {
  list-style-type: disc;
  padding-left: 20px;
  margin: 8px 0;
}

:deep(.ck-content) li {
  margin-bottom: 4px;
}
</style>
